import { defineStore } from 'pinia'
import type { Ref } from 'vue'
import type { Server, ServerForm } from '~/types/servers'
import { AdminServersCreateModal, AdminServersEditModal } from '#components'

export const useServersStore = defineStore('servers', () => {
  const log = useLogger('store:servers')
  const { get, post, put, delete: del } = useAuthenticatedFetch()
  const businessStore = useBusinessStore()
  const { businessId } = storeToRefs(businessStore)

  const nuxtApp = useNuxtApp()
  const { t } = nuxtApp.$i18n
  const { $confirm, $dialog } = nuxtApp

  // State
  const servers: Ref<Server[]> = ref([])
  const loading: Ref<boolean> = ref(false)

  // Filter states
  const searchTerm: Ref<string> = ref('')
  const showInactive: Ref<boolean> = ref(false)

  // Core data fetching
  async function fetchServers(): Promise<void> {
    if (!businessId.value) return

    loading.value = true
    try {
      const queryParam = showInactive.value ? '?include_inactive=true' : ''
      const res = await get(`/api/business/${businessId.value}/servers${queryParam}`)
      servers.value = res.data || []
    }
    catch (error: any) {
      log.error('Error fetching servers:', error)

      if (error.name === 'OfflineError') {
        useErrorToast('App is offline - using cached servers')
      }
      else if (error.name === 'AuthenticationRequired') {
        useErrorToast('Please sign in to load servers')
      }
      else {
        useErrorToast('Failed to load servers')
      }
    }
    finally {
      loading.value = false
    }
  }

  async function fetchServerById(serverId: number): Promise<Server | null> {
    if (!businessId.value) return null

    try {
      const res = await get(`/api/business/${businessId.value}/servers/${serverId}`)
      return res.data || null
    }
    catch (error: any) {
      log.error('Error fetching server:', error)

      if (error.name === 'OfflineError') {
        useErrorToast('App is offline')
      }
      else if (error.name === 'AuthenticationRequired') {
        useErrorToast('Please sign in')
      }
      else {
        useErrorToast('Failed to load server')
      }
      return null
    }
  }

  // CRUD operations
  async function createServer(formData: ServerForm): Promise<boolean> {
    if (!businessId.value) {
      useErrorToast('Business ID not found')
      return false
    }

    try {
      const res = await post(`/api/business/${businessId.value}/servers`, formData)

      if (res.success) {
        useSuccessToast('Server created successfully')
        await fetchServers()
        return true
      }
      else {
        const errorMsg = res.message ? `Failed to create server: ${res.message}` : 'Failed to create server'
        useErrorToast(errorMsg)
        return false
      }
    }
    catch (error: any) {
      log.error('Error creating server:', error)

      if (error.name === 'OfflineError') {
        useErrorToast('Cannot create server while offline')
      }
      else if (error.name === 'AuthenticationRequired') {
        useErrorToast('Please sign in to create servers')
      }
      else {
        const errorMessage = error?.data?.message || error?.message || 'Unknown error'
        useErrorToast(`Failed to create server: ${errorMessage}`)
      }
      return false
    }
  }

  async function updateServer(serverId: number, formData: Partial<ServerForm>): Promise<boolean> {
    if (!businessId.value) {
      useErrorToast('Business ID not found')
      return false
    }

    try {
      const res = await put(`/api/business/${businessId.value}/servers/${serverId}`, formData)

      if (res.success) {
        useSuccessToast('Server updated successfully')
        await fetchServers()
        return true
      }
      else {
        const errorMsg = res.message ? `Failed to update server: ${res.message}` : 'Failed to update server'
        useErrorToast(errorMsg)
        return false
      }
    }
    catch (error: any) {
      log.error('Error updating server:', error)

      if (error.name === 'OfflineError') {
        useErrorToast('Cannot update server while offline')
      }
      else if (error.name === 'AuthenticationRequired') {
        useErrorToast('Please sign in to update servers')
      }
      else {
        const errorMessage = error?.data?.message || error?.message || 'Unknown error'
        useErrorToast(`Failed to update server: ${errorMessage}`)
      }
      return false
    }
  }

  async function activateServer(serverId: number): Promise<boolean> {
    if (!businessId.value) {
      useErrorToast('Business ID not found')
      return false
    }

    try {
      const res = await put(`/api/business/${businessId.value}/servers/${serverId}`, { active: true })

      if (res.success) {
        useSuccessToast('Server activated successfully')
        await fetchServers()
        return true
      }
      else {
        useErrorToast('Failed to activate server')
        return false
      }
    }
    catch (error: any) {
      log.error('Error activating server:', error)
      useErrorToast('Failed to activate server')
      return false
    }
  }

  async function deleteServer(serverId: number, hardDelete = false): Promise<boolean> {
    if (!businessId.value) {
      useErrorToast('Business ID not found')
      return false
    }

    try {
      const queryParam = hardDelete ? '?hard=true' : ''
      const res = await del(`/api/business/${businessId.value}/servers/${serverId}${queryParam}`)

      if (res.success) {
        useSuccessToast(hardDelete ? 'Server deleted successfully' : 'Server deactivated successfully')
        await fetchServers()
        return true
      }
      else {
        useErrorToast('Failed to delete server')
        return false
      }
    }
    catch (error: any) {
      log.error('Error deleting server:', error)

      if (error.name === 'OfflineError') {
        useErrorToast('Cannot delete server while offline')
      }
      else if (error.name === 'AuthenticationRequired') {
        useErrorToast('Please sign in to delete servers')
      }
      else {
        useErrorToast('Failed to delete server')
      }
      return false
    }
  }

  // Dialog and confirmation methods
  async function openCreateModal(): Promise<void> {
    $dialog.open(AdminServersCreateModal, {
      data: {
        businessId: businessId.value,
      },
      props: {
        header: t('Add new server'),
        ...useDynamicDialogSettings,
      },
      onClose: async () => {
        await fetchServers()
      },
    })
  }

  async function openEditModal(server: Server): Promise<void> {
    $dialog.open(AdminServersEditModal, {
      data: {
        serverId: server.id,
        businessId: businessId.value,
      },
      props: {
        header: t('Edit server'),
        ...useDynamicDialogSettings,
      },
      onClose: async () => {
        await fetchServers()
      },
    })
  }

  async function deactivateServerWithConfirm(serverId: number): Promise<void> {
    $confirm.require({
      group: 'inline',
      message: t('Do you want to deactivate this server?'),
      icon: 'pi pi-info-circle',
      rejectProps: {
        label: t('Cancel'),
        severity: 'secondary',
        outlined: true,
      },
      acceptProps: {
        label: t('Deactivate'),
        severity: 'warning',
      },
      accept: async () => {
        await deleteServer(serverId, false)
      },
      reject: () => {
        // User cancelled
      },
    })
  }

  async function deleteServerWithConfirm(serverId: number): Promise<void> {
    $confirm.require({
      group: 'inline',
      message: t('Do you want to permanently delete this server?'),
      icon: 'pi pi-exclamation-triangle',
      rejectProps: {
        label: t('Cancel'),
        severity: 'secondary',
        outlined: true,
      },
      acceptProps: {
        label: t('Delete'),
        severity: 'danger',
      },
      accept: async () => {
        await deleteServer(serverId, true)
      },
      reject: () => {
        // User cancelled
      },
    })
  }

  // Computed properties for filtering
  const filteredServers = computed(() => {
    let filtered = servers.value

    // Filter by search term
    if (searchTerm.value.trim()) {
      const term = searchTerm.value.toLowerCase()
      filtered = filtered.filter(server =>
        server.name.toLowerCase().includes(term)
        || server.employee_number?.toLowerCase().includes(term),
      )
    }

    // Filter by active status
    if (!showInactive.value) {
      filtered = filtered.filter(server => server.active)
    }

    return filtered
  })

  const totalServers = computed(() => servers.value.length)

  const activeServers = computed(() =>
    servers.value.filter(server => server.active).length,
  )

  const inactiveServers = computed(() =>
    servers.value.filter(server => !server.active).length,
  )

  // FormKit select options
  const serverOptions = computed(() =>
    servers.value
      .filter(server => server.active)
      .map(server => ({
        label: server.name,
        value: server.id,
      })),
  )

  // Utility functions
  function getServerById(id: number): Server | undefined {
    return servers.value.find(server => server.id === id)
  }

  function getServerByName(name: string): Server | undefined {
    return servers.value.find(server => server.name === name)
  }

  // Search and filter methods
  function setSearchTerm(term: string): void {
    searchTerm.value = term
  }

  function toggleShowInactive(): void {
    showInactive.value = !showInactive.value
    fetchServers()
  }

  function clearFilters(): void {
    searchTerm.value = ''
    showInactive.value = false
  }

  // Reset method to clear all state
  function reset(): void {
    servers.value = []
    loading.value = false
    searchTerm.value = ''
    showInactive.value = false
  }

  return {
    // State
    servers,
    loading,
    searchTerm,
    showInactive,

    // Computed
    filteredServers,
    totalServers,
    activeServers,
    inactiveServers,
    serverOptions,

    // Core methods
    fetchServers,
    fetchServerById,
    createServer,
    updateServer,
    activateServer,
    deleteServer,

    // Dialog methods
    openCreateModal,
    openEditModal,
    deactivateServerWithConfirm,
    deleteServerWithConfirm,

    // Utility methods
    getServerById,
    getServerByName,

    // Filter methods
    setSearchTerm,
    toggleShowInactive,
    clearFilters,

    // Reset method
    reset,
  }
}, {
  persist: {
    key: 'servers',
    storage: localStorage,
    paths: ['servers'],
  },
})

export const createServersStore = async (businessIdParam: string) => {
  const serversStore = useServersStore()
  const businessStore = useBusinessStore()

  businessStore.setBusinessId(businessIdParam)
  await serversStore.fetchServers()

  return serversStore
}
